import {DeleteOutlined} from '@ant-design/icons';
import {Button, Form, Input} from 'antd';
import {Rule} from 'antd/es/form';
import {FormListFieldData} from 'antd/es/form/FormList';
import React, {FC} from 'react';

import {DateInput} from 'components/@common/inputs/date-input';
import {CustomDecimalInput} from 'components/form/components/custom-decimal-input';
import {CustomSelect} from 'components/form/inputs/custom-select';
import {DATE_DMY} from 'shared/constants/date-format';
import {normalizeFieldMaxLength} from 'shared/utils';

import {useLookupCheck} from '../../hooks';
import {maxSizeRule} from '../../lib';
import {
    FACTOR_LEVEL_LOOKUP_URL,
    FACTOR_TYPE_CODE_LOOKUP_URL,
    COMPANY_LEVEL_LOOKUP_CODE,
    FACTOR_TYPE_CODE_TEN_LOOKUP_CODE,
    FACTOR_TYPE_CODE_EXPORT_BAN_LOOKUP_CODE,
    FACTOR_TYPE_CODE_IMPORT_BAN_LOOKUP_CODE,
    FACTOR_TYPE_CODE_TRADE_RESTRICTIONS_LOOKUP_CODE,
    PRODUCTS_REQUIRED_ERROR_MESSAGE,
} from '../../model/constants';
import {ProductsList} from './products-list';

interface IFactorItemProps {
    field: FormListFieldData;
    onRemove: (index: number) => void;
    isReadOnly?: boolean;
}

export const FactorItem: FC<IFactorItemProps> = ({
    field,
    onRemove,
    isReadOnly = false
}: IFactorItemProps) => {
    const isCompanyLevel = useLookupCheck(FACTOR_LEVEL_LOOKUP_URL, COMPANY_LEVEL_LOOKUP_CODE);
    const isExitFromInternationalProjects = useLookupCheck(FACTOR_TYPE_CODE_LOOKUP_URL, FACTOR_TYPE_CODE_TEN_LOOKUP_CODE);
    const isExportBan = useLookupCheck(FACTOR_TYPE_CODE_LOOKUP_URL, FACTOR_TYPE_CODE_EXPORT_BAN_LOOKUP_CODE);
    const isImportBan = useLookupCheck(FACTOR_TYPE_CODE_LOOKUP_URL, FACTOR_TYPE_CODE_IMPORT_BAN_LOOKUP_CODE);
    const isTradeRestrictions = useLookupCheck(FACTOR_TYPE_CODE_LOOKUP_URL, FACTOR_TYPE_CODE_TRADE_RESTRICTIONS_LOOKUP_CODE);

    const validateProductsRequired = (factorTypeCodeId: number | null | undefined): Rule => ({
        validator: (_: Rule, value: any[]) => {
            const isRequired = factorTypeCodeId && (
                isExportBan(factorTypeCodeId) ||
                isImportBan(factorTypeCodeId) ||
                isTradeRestrictions(factorTypeCodeId)
            );

            if (isRequired && (!value || value.length === 0)) {
                return Promise.reject(new Error(PRODUCTS_REQUIRED_ERROR_MESSAGE));
            }

            return Promise.resolve();
        },
    });

    return (
        <div className="factor-item-block">
            <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onRemove(field.name)}
                disabled={isReadOnly}
                className="delete-button"
            />

            <div className="form-flex-row">
                <Form.Item
                    {...field}
                    name={[field.name, 'factorNumber']}
                    label="Номер фактора (санкции)"
                    rules={[{required: true, message: 'Обязательное поле'}]}
                    normalize={(value) => normalizeFieldMaxLength(value, 6)}
                >
                    <Input
                        placeholder="Введите значение"
                        type="number"
                        autoComplete="off"
                        disabled={isReadOnly}
                    />
                </Form.Item>
                <Form.Item
                    {...field}
                    name={[field.name, 'factorName']}
                    label="Наименование фактора (сутевое краткое содержание)"
                    rules={[
                        {required: true, message: 'Обязательное поле'},
                        maxSizeRule(500),
                    ]}
                >
                    <Input disabled={isReadOnly} placeholder="Введите значение" autoComplete="off" />
                </Form.Item>
                <Form.Item
                    {...field}
                    name={[field.name, 'factorTypeCodeId']}
                    label="Вид фактора"
                    rules={[{required: true, message: 'Обязательное поле'}]}
                >
                     <CustomSelect settings={{
                         showSearch: true,
                         url: FACTOR_TYPE_CODE_LOOKUP_URL,
                         valuePath: 'id',
                         labelPath: 'description',
                         isDisabled: isReadOnly,
                    }}
                    />
                </Form.Item>
            </div>

            <div className="form-flex-row">
                <Form.Item
                    {...field}
                    name={[field.name, 'factorPackage']}
                    label="Пакет санкций"
                    normalize={(value) => normalizeFieldMaxLength(value, 2)}
                >
                    <Input disabled={isReadOnly} placeholder="Введите значение" type="number" autoComplete="off" />
                </Form.Item>
                <Form.Item
                    {...field}
                    name={[field.name, 'factorCountryCode']}
                    label="Код страны, принявшей санкции"
                    normalize={(value) => normalizeFieldMaxLength(value, 3)}
                >
                    <Input disabled={isReadOnly} placeholder="Введите значение" type="number" autoComplete="off" />
                </Form.Item>
                <Form.Item
                    {...field}
                    name={[field.name, 'factorLevelId']}
                    label="Уровень принятия санкций"
                >
                    <CustomSelect
                        settings={{
                            showSearch: true,
                            url: FACTOR_LEVEL_LOOKUP_URL,
                            valuePath: 'id',
                            labelPath: 'description',
                            isDisabled: isReadOnly,
                        }}
                    />
                </Form.Item>
            </div>

            <div className="form-flex-row">
                <Form.Item
                    {...field}
                    name={[field.name, 'decisionBodyName']}
                    rules={[
                        maxSizeRule(1000),
                    ]}
                    label="Орган принятия санкции"
                >
                    <Input disabled={isReadOnly} placeholder="Введите значение" autoComplete="off" />
                </Form.Item>
                <Form.Item
                    shouldUpdate={(prevValues, currentValues) => {
                        const prevFactorLevel = prevValues.factors?.[field.name]?.factorLevelId;
                        const currentFactorLevel = currentValues.factors?.[field.name]?.factorLevelId;
                        return prevFactorLevel !== currentFactorLevel;
                    }}
                    noStyle
                >
                    {({getFieldValue}) => {
                        const factorLevelId = getFieldValue(['factors', field.name, 'factorLevelId']);
                        const isDisabled = !isCompanyLevel(factorLevelId);

                        return (
                            <Form.Item
                                {...field}
                                name={[field.name, 'companyName']}
                                label="Наименование иностранной компании"
                                required={!isDisabled}
                                rules={!isDisabled ? [
                                    {required: true, message: 'Обязательное поле'},
                                    maxSizeRule(1000),
                                ] : []}
                                dependencies={[['factors', field.name, 'factorLevelId']]}
                            >
                                <Input 
                                    placeholder="Введите значение" 
                                    autoComplete="off" 
                                    disabled={isDisabled || isReadOnly}
                                />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
            </div>

            <div className="form-flex-row">
                <Form.Item
                    {...field}
                    name={[field.name, 'docName']}
                    rules={[
                        maxSizeRule(1000),
                    ]}
                    label="Наименование документа, которым установлены санкционные ограничения"
                >
                    <Input disabled={isReadOnly} placeholder="Введите значение" autoComplete="off" />
                </Form.Item>
                <Form.Item
                    shouldUpdate={(prevValues, currentValues) => {
                        const prevFactorTypeCode = prevValues.factors?.[field.name]?.factorTypeCodeId;
                        const currentFactorTypeCode = currentValues.factors?.[field.name]?.factorTypeCodeId;
                        return prevFactorTypeCode !== currentFactorTypeCode;
                    }}
                    noStyle
                >
                    {({getFieldValue}) => {
                        const factorTypeCodeId = getFieldValue(['factors', field.name, 'factorTypeCodeId']);
                        const isDisabled = !isExitFromInternationalProjects(factorTypeCodeId);

                        return (
                            <Form.Item
                                {...field}
                                name={[field.name, 'projectName']}
                                label="Наименование международного проекта"
                                required={!isDisabled}
                                rules={!isDisabled ? [{required: true, message: 'Обязательное поле'}] : []}
                                dependencies={[['factors', field.name, 'factorTypeCodeId']]}
                            >
                                <Input 
                                    placeholder="Введите значение" 
                                    autoComplete="off" 
                                    disabled={isDisabled || isReadOnly}
                                />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
                <Form.Item
                    shouldUpdate={(prevValues, currentValues) => {
                        const prevFactorTypeCode = prevValues.factors?.[field.name]?.factorTypeCodeId;
                        const currentFactorTypeCode = currentValues.factors?.[field.name]?.factorTypeCodeId;
                        return prevFactorTypeCode !== currentFactorTypeCode;
                    }}
                    noStyle
                >
                    {({getFieldValue}) => {
                        const factorTypeCodeId = getFieldValue(['factors', field.name, 'factorTypeCodeId']);
                        const isDisabled = !isExitFromInternationalProjects(factorTypeCodeId);

                        return (
                            <Form.Item
                                {...field}
                                name={[field.name, 'share']}
                                rules={[
                                    () => ({
                                        validator(_, value) {
                                            if (!value) {
                                                return Promise.resolve();
                                            }

                                            const pattern = /^\d{0,2}(?:\.\d{0,3})?$/;
                                            if (!pattern.test(value))
                                                return Promise.reject(new Error('Допустим ввод только цифр и запятой. ' +
                                                    'Не более 2 цифр до запятой и 3 цифр после запятой.'));
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                                label="Доля участия иностранной компании, которая вышла из-за санкций из совместного проекта, в международном проекте"
                            >
                                <CustomDecimalInput
                                    placeholder="Введите значение"
                                    autoComplete="off"
                                    disabled={isDisabled || isReadOnly}
                                />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
            </div>

            <div className="form-flex-row">
                <Form.Item
                    {...field}
                    name={[field.name, 'publicDate']}
                    label="Дата публикации"
                >
                    <DateInput disabled={isReadOnly} format={DATE_DMY} />
                </Form.Item>
                <Form.Item
                    {...field}
                    name={[field.name, 'effectiveDate']}
                    label="Дата вступления в силу"
                >
                    <DateInput disabled={isReadOnly} format={DATE_DMY} />
                </Form.Item>
                <Form.Item
                    {...field}
                    name={[field.name, 'startDate']}
                    label="Дата начала действия"
                >
                    <DateInput disabled={isReadOnly} format={DATE_DMY} />
                </Form.Item>
                <Form.Item
                    {...field}
                    name={[field.name, 'cancelDate']}
                    label="Дата отмены"
                >
                    <DateInput disabled={isReadOnly} format={DATE_DMY} />
                </Form.Item>
            </div>

            <ProductsList
                factorFieldName={field.name}
                validateProductsRequired={validateProductsRequired}
                isReadOnly={isReadOnly}
            />
        </div>
    );
};
